import { supabase } from '@/integrations/supabase/client';
import { compensationService } from '@/services/compensation/compensationService';
import type { PaidLeaveEntry } from '@/services/compensation/types';
import type { EmployeePayrollData } from './types';
import type { PendingLeaveWarning, EmployeeLeavePayrollData } from './leavePayrollService';

export interface PayrollPreviewLineItem {
  description: string;
  hours: number;
  rate: number;
  amount: number;
  category: 'earnings' | 'deductions' | 'taxes';
  isServiceCharge?: boolean;
  isPaidLeave?: boolean;
}

export interface EmployeePayrollPreview {
  profile_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  total_hours: number;
  regular_hours: number;
  overtime_hours: number;
  holiday_hours: number;
  paid_leave_hours: number;
  line_items: PayrollPreviewLineItem[];
  gross_pay: number;
  total_deductions: number;
  net_pay: number;
  service_charge: number;
  pending_leave_warnings?: PendingLeaveWarning[];
}

export interface SkippedEmployee {
  profile_id: string;
  first_name: string;
  last_name: string;
  email: string;
  reason: 'existing_payslip';
  existing_payslip_id?: string;
}

export interface ZeroPayEmployee {
  profile_id: string;
  first_name: string;
  last_name: string;
  email: string;
  reason: 'no_compensation_rules';
}

export interface PayrollPreviewTotals {
  total_employees: number;
  total_hours: number;
  total_gross: number;
  total_deductions: number;
  total_net: number;
  total_service_charge: number;
  total_paid_leave_hours: number;
}

export interface PayrollPreviewResponse {
  employees: EmployeePayrollPreview[];
  totals: PayrollPreviewTotals;
  service_charge_period?: {
    period_label: string;
    total_pool_amount: number;
  };
  skipped_employees?: SkippedEmployee[];
  zero_pay_employees?: ZeroPayEmployee[];
  has_pending_leave_warnings?: boolean;
  service_charge_warning?: string;
}

export const payrollPreviewService = {
  /**
   * Check which employees already have payslips for the given period
   */
  async checkExistingPayslips(
    profileIds: string[],
    startDate: string,
    endDate: string
  ): Promise<Map<string, string>> {
    if (profileIds.length === 0) return new Map();

    const { data: existingPayslips, error } = await supabase
      .from('payslips')
      .select('id, profile_id')
      .in('profile_id', profileIds)
      .eq('start_date', startDate)
      .eq('end_date', endDate);

    // Fail closed: a failed read must not be mistaken for "no existing payslips".
    if (error) {
      throw new Error(`Failed to check existing payslips: ${error.message}`);
    }

    const existingMap = new Map<string, string>();
    existingPayslips?.forEach(p => {
      existingMap.set(p.profile_id, p.id);
    });

    return existingMap;
  },

  async generatePreview(
    startDate: string,
    endDate: string,
    selectedEmployees: EmployeePayrollData[]
  ): Promise<PayrollPreviewResponse> {
    // Get current user's company
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('user_id', user.id)
      .single();

    if (!profile) throw new Error('Profile not found');

    // Fetch currency code from company settings
    const { data: companySettings } = await supabase
      .from('company_settings')
      .select('currency_code')
      .eq('company_id', profile.company_id)
      .single();

    const currencyCode = companySettings?.currency_code || 'USD';

    // Check for existing payslips BEFORE generating preview
    const profileIds = selectedEmployees.map(emp => emp.profile_id);
    const existingPayslipsMap = await this.checkExistingPayslips(profileIds, startDate, endDate);

    // Separate employees into those who can be processed vs skipped
    const skippedEmployees: SkippedEmployee[] = [];
    const eligibleEmployees: EmployeePayrollData[] = [];

    for (const emp of selectedEmployees) {
      const existingPayslipId = existingPayslipsMap.get(emp.profile_id);
      if (existingPayslipId) {
        skippedEmployees.push({
          profile_id: emp.profile_id,
          first_name: emp.first_name,
          last_name: emp.last_name,
          email: emp.email,
          reason: 'existing_payslip',
          existing_payslip_id: existingPayslipId,
        });
      } else {
        eligibleEmployees.push(emp);
      }
    }

    // Batch-check active compensation rules for all eligible employees.
    // Apply the same effective-date filter as the edge function (L1154–1162):
    // rule must be effective on or before endDate, and not yet expired.
    const zeroPayEmployees: ZeroPayEmployee[] = [];
    let previewEligibleEmployees = eligibleEmployees;

    if (eligibleEmployees.length > 0) {
      const eligibleProfileIds = eligibleEmployees.map(emp => emp.profile_id);
      const periodEnd = new Date(endDate);

      const { data: activeRuleAssignments, error: assignmentError } = await supabase
        .from('compensation_rule_assignments')
        .select(`
          profile_id,
          compensation_rules!inner (
            effective_from,
            effective_to,
            is_active
          )
        `)
        .in('profile_id', eligibleProfileIds)
        .eq('is_active', true)
        .eq('compensation_rules.is_active', true);

      // Fail closed: without this, a failed read classifies EVERY employee as
      // "no compensation rules" and the operator sees a fake zero-pay warning.
      if (assignmentError) {
        throw new Error(`Failed to load compensation assignments: ${assignmentError.message}`);
      }

      // Build set of profile IDs that have at least one rule valid for this period
      const profilesWithRules = new Set<string>();
      for (const assignment of activeRuleAssignments || []) {
        const rule = (assignment as any).compensation_rules;
        if (!rule) continue;
        const effectiveFrom = new Date(rule.effective_from);
        const effectiveTo = rule.effective_to ? new Date(rule.effective_to) : null;
        // Mirror edge function filter: rule must start on/before period end,
        // and must not have expired before period end.
        if (effectiveFrom > periodEnd) continue;
        if (effectiveTo && effectiveTo < periodEnd) continue;
        profilesWithRules.add(assignment.profile_id);
      }

      // Partition: employees with no valid rules go to zeroPayEmployees
      previewEligibleEmployees = [];
      for (const emp of eligibleEmployees) {
        if (!profilesWithRules.has(emp.profile_id)) {
          zeroPayEmployees.push({
            profile_id: emp.profile_id,
            first_name: emp.first_name,
            last_name: emp.last_name,
            email: emp.email,
            reason: 'no_compensation_rules',
          });
        } else {
          previewEligibleEmployees.push(emp);
        }
      }
    }

    // Only generate preview for eligible employees
    let employeePreviews: EmployeePayrollPreview[] = [];
    let serviceChargeWarning: string | undefined;
    
    if (previewEligibleEmployees.length > 0) {
      const eligibleProfileIds = previewEligibleEmployees.map(emp => emp.profile_id);
      
      // Fetch leave data via edge function (service role) to bypass RLS restrictions
      // This ensures the preview sees the same leave data as the actual pay run
      const { data: leaveResponse, error: leaveError } = await supabase.functions.invoke(
        'payroll-leave-data',
        {
          body: {
            profileIds: eligibleProfileIds,
            startDate,
            endDate,
          },
        }
      );

      if (leaveError) {
        console.error('[payrollPreviewService] Error fetching leave data:', leaveError);
        throw new Error(`Failed to fetch leave data: ${leaveError.message}`);
      }

      // Convert edge function response to Map<string, EmployeeLeavePayrollData>
      const leaveDataRecord = leaveResponse?.data || {};
      const leaveData = new Map<string, EmployeeLeavePayrollData>();
      for (const [profileId, empLeave] of Object.entries(leaveDataRecord)) {
        leaveData.set(profileId, empLeave as EmployeeLeavePayrollData);
      }
      
      // Build paidLeaveMap from leave data to pass into calculation engine
      const paidLeaveMap = new Map<string, PaidLeaveEntry[]>();
      for (const [profileId, empLeaveData] of leaveData.entries()) {
        const entries: PaidLeaveEntry[] = [];
        for (const leave of empLeaveData.approved_leave) {
          // Use pre-computed hours from leavePayrollService (business_hours-derived)
          if (leave.computed_hours > 0) {
            entries.push({
              leaveType: leave.leave_type,
              startDate: leave.start_date,
              endDate: leave.end_date,
              hours: leave.computed_hours,
            });
          }
        }
        if (entries.length > 0) {
          paidLeaveMap.set(profileId, entries);
        }
      }

      // Pass leave data INTO the engine so it's included BEFORE tax/deduction calculations
      const payrollResults = await compensationService.generatePayrollPreview({
        profileIds: eligibleProfileIds,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        currencyCode,
        paidLeaveMap,
        onServiceChargeWarning: (warning) => { serviceChargeWarning = warning; },
      });

      // Transform results to our preview format
      employeePreviews = payrollResults.map((result) => {
        const employee = previewEligibleEmployees.find(emp => emp.profile_id === result.profileId);
        if (!employee) throw new Error(`Employee not found: ${result.profileId}`);

        // Get leave warnings for this employee (informational only)
        const employeeLeave = leaveData.get(result.profileId);
        const pendingWarnings = employeeLeave?.pending_warnings || [];
        const paidLeaveHours = result.calculation.calculationSummary.paidLeaveHours || 0;

        // Convert calculation line items to our preview format
        const lineItems: PayrollPreviewLineItem[] = result.calculation.lineItems.map(item => {
          const isServiceCharge = item.description.includes('Service Charge –');
          
          return {
            description: item.description,
            hours: item.hours_applied || 0,
            rate: item.rate_applied || 0,
            amount: item.gross_amount,
            category: item.category,
            isServiceCharge,
            isPaidLeave: item.line_item_type === 'paid_leave',
          };
        });

        // Extract service charge from line items
        const serviceCharge = lineItems
          .filter(item => item.isServiceCharge)
          .reduce((sum, item) => sum + item.amount, 0);

        // Use engine's computed values directly (leave is already included)
        const gross_pay = result.calculation.totalGrossPay;
        const total_deductions = result.calculation.totalDeductions;
        const net_pay = result.calculation.netPay;

        return {
          profile_id: result.profileId,
          first_name: employee.first_name,
          last_name: employee.last_name,
          email: employee.email,
          role: employee.role,
          total_hours: result.calculation.calculationSummary.regularHours + 
                      result.calculation.calculationSummary.overtimeHours + 
                      result.calculation.calculationSummary.holidayHours +
                      (result.calculation.calculationSummary.nonScheduledDayHours || 0) +
                      paidLeaveHours,
          regular_hours: result.calculation.calculationSummary.regularHours,
          overtime_hours: result.calculation.calculationSummary.overtimeHours,
          holiday_hours: result.calculation.calculationSummary.holidayHours,
          paid_leave_hours: paidLeaveHours,
          line_items: lineItems,
          gross_pay,
          total_deductions,
          net_pay,
          service_charge: serviceCharge,
          pending_leave_warnings: pendingWarnings.length > 0 ? pendingWarnings : undefined,
        };
      });
    }

    // Extract service charge period info from line items if present
    let serviceChargePeriod: { period_label: string; total_pool_amount: number } | undefined;
    const firstServiceChargeItem = employeePreviews[0]?.line_items.find(item => item.isServiceCharge);
    if (firstServiceChargeItem) {
      // Extract period label from description (format: "Service Charge – <period_label>")
      const match = firstServiceChargeItem.description.match(/Service Charge – (.+)/);
      if (match) {
        serviceChargePeriod = {
          period_label: match[1],
          total_pool_amount: employeePreviews.reduce((sum, emp) => sum + emp.service_charge, 0),
        };
      }
    }

    // Calculate totals (only for eligible employees)
    const totals: PayrollPreviewTotals = {
      total_employees: employeePreviews.length,
      total_hours: employeePreviews.reduce((sum, emp) => sum + emp.total_hours, 0),
      total_gross: employeePreviews.reduce((sum, emp) => sum + emp.gross_pay, 0),
      total_deductions: employeePreviews.reduce((sum, emp) => sum + emp.total_deductions, 0),
      total_net: employeePreviews.reduce((sum, emp) => sum + emp.net_pay, 0),
      total_service_charge: employeePreviews.reduce((sum, emp) => sum + emp.service_charge, 0),
      total_paid_leave_hours: employeePreviews.reduce((sum, emp) => sum + emp.paid_leave_hours, 0),
    };

    // Check if any employees have pending leave warnings
    const hasPendingLeaveWarnings = employeePreviews.some(
      emp => emp.pending_leave_warnings && emp.pending_leave_warnings.length > 0
    );

    return {
      employees: employeePreviews,
      totals,
      service_charge_period: serviceChargePeriod,
      skipped_employees: skippedEmployees.length > 0 ? skippedEmployees : undefined,
      zero_pay_employees: zeroPayEmployees.length > 0 ? zeroPayEmployees : undefined,
      has_pending_leave_warnings: hasPendingLeaveWarnings || undefined,
      service_charge_warning: serviceChargeWarning,
    };
  },
};
